import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  CalendarClock,
  CheckCircle2,
  XCircle,
  RotateCcw,
  Edit3,
  Loader2,
  RefreshCw,
  LogOut,
  Mail,
  FileText,
  Link as LinkIcon,
  Send,
  Trash2,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  adminListPosts,
  adminEditPost,
  adminPostAction,
  adminGenerateBatch,
  adminLogout,
  adminMe,
  adminSummary,
  adminBulkAction,
  adminCreatePreviewToken,
  setAdminToken,
  getAdminToken,
} from "@/lib/api";
import { useSessionRenewal } from "@/hooks/useSessionRenewal";
import SEO from "@/components/SEO";

const STATUS_ORDER = [
  "Pending Review",
  "Needs Edits",
  "Approved",
  "Scheduled",
  "Published",
  "Rejected",
];

const STATUS_STYLES = {
  "Pending Review": "bg-[#FEF3C7] text-[#92400E]",
  "Needs Edits": "bg-[#FEE2E2] text-[#991B1B]",
  Approved: "bg-[#E0F2FE] text-[#075985]",
  Scheduled: "bg-[#DBEAFE] text-[#1E40AF]",
  Published: "bg-[#DCFCE7] text-[#166534]",
  Rejected: "bg-[#F3F4F6] text-[#374151]",
};

function StatusPill({ status }) {
  return (
    <span
      data-testid={`status-pill-${status.toLowerCase().replace(/\s+/g, "-")}`}
      className={`inline-flex items-center px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] ${
        STATUS_STYLES[status] || "bg-[#F3F4F6] text-[#374151]"
      }`}
    >
      {status}
    </span>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function AdminBlog() {
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [posts, setPosts] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState("");
  const [active, setActive] = useState(null); // currently expanded post
  const [editForm, setEditForm] = useState({});
  const [busyId, setBusyId] = useState("");
  const [generating, setGenerating] = useState(false);
  const [summary, setSummary] = useState(null);
  // E7 — bulk selection
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  useEffect(() => {
    if (!getAdminToken()) {
      nav("/admin/login", { replace: true });
      return;
    }
    adminMe()
      .then((res) => {
        // Backfill expires_at into localStorage (covers sessions issued before
        // the refresh feature existed).
        if (res?.expires_at) {
          setAdminToken(getAdminToken(), res.expires_at);
        }
      })
      .catch(() => {
        setAdminToken("");
        nav("/admin/login", { replace: true });
      });
  }, [nav]);

  useSessionRenewal({
    onExpired: () => {
      setAdminToken("");
      nav("/admin/login", { replace: true });
    },
  });

  const load = useCallback(
    async (statusFilter = filter) => {
      setLoading(true);
      try {
        const data = await adminListPosts(statusFilter || undefined);
        setPosts(data.posts || []);
        setCounts(data.counts || {});
        const s = await adminSummary();
        setSummary(s);
      } catch (err) {
        if (err?.response?.status === 401) {
          setAdminToken("");
          nav("/admin/login", { replace: true });
        } else {
          toast.error("Failed to load posts.");
        }
      } finally {
        setLoading(false);
      }
    },
    [filter, nav]
  );

  useEffect(() => {
    load();
  }, [load]);

  // Clear bulk selection whenever the post list changes (e.g. filter switch).
  useEffect(() => {
    setSelected(new Set());
  }, [filter]);

  const onGenerate = async () => {
    if (
      !window.confirm(
        "Generate a new quarterly batch of 12 drafts? This runs in the background and takes a few minutes."
      )
    )
      return;
    setGenerating(true);
    try {
      await adminGenerateBatch();
      toast.success("Generation started — refresh in a few minutes.");
    } catch {
      toast.error("Failed to start generation.");
    } finally {
      setGenerating(false);
    }
  };

  const onLogout = async () => {
    try {
      await adminLogout();
    } catch (err) {
      // Token may already be expired/invalid — proceed to clear local state.
      console.warn("adminLogout failed (continuing with local sign-out):", err);
    }
    setAdminToken("");
    nav("/admin/login", { replace: true });
  };

  const beginEdit = (post) => {
    setActive(post.id);
    // E1 — Try to restore an unsaved local draft. If found, prefer it.
    const draftKey = `ashlan_draft_${post.id}`;
    let draftRaw = null;
    try { draftRaw = localStorage.getItem(draftKey); } catch { /* ignore */ }
    const baseline = {
      title: post.title,
      seo_title: post.seo_title,
      meta_description: post.meta_description,
      slug: post.slug,
      excerpt: post.excerpt,
      featured_image_url: post.featured_image_url,
      content_markdown: post.content_markdown,
      cta_secondary: post.cta_secondary || "",
      admin_notes: post.admin_notes || "",
    };
    if (draftRaw) {
      try {
        const draft = JSON.parse(draftRaw);
        if (draft && draft.savedAt && (Date.now() - draft.savedAt < 14 * 24 * 3600 * 1000)) {
          if (window.confirm(
            `Unsaved draft found for this post (last edit ${new Date(draft.savedAt).toLocaleString()}). Restore it?`
          )) {
            setEditForm({ ...baseline, ...draft.form });
            return;
          }
          // User declined — drop the draft.
          try { localStorage.removeItem(draftKey); } catch { /* ignore */ }
        }
      } catch (e) { console.warn("draft parse failed", e); }
    }
    setEditForm(baseline);
  };

  // E1 — Auto-save the edit form to localStorage on every change (debounced).
  useEffect(() => {
    if (!active || !editForm || !Object.keys(editForm).length) return undefined;
    const t = setTimeout(() => {
      try {
        localStorage.setItem(
          `ashlan_draft_${active}`,
          JSON.stringify({ form: editForm, savedAt: Date.now() })
        );
      } catch (e) { console.warn("auto-save failed", e); }
    }, 800);
    return () => clearTimeout(t);
  }, [active, editForm]);

  const saveEdit = async (id) => {
    setBusyId(id);
    try {
      await adminEditPost(id, editForm);
      toast.success("Saved.");
      // E1 — clear the local draft after a successful save
      try { localStorage.removeItem(`ashlan_draft_${id}`); } catch { /* ignore */ }
      setActive(null);
      await load();
    } catch {
      toast.error("Save failed.");
    } finally {
      setBusyId("");
    }
  };

  const doAction = async (id, action, extra = {}) => {
    setBusyId(id);
    try {
      await adminPostAction(id, { action, ...extra });
      toast.success(`Status updated: ${action}.`);
      await load();
    } catch {
      toast.error("Action failed.");
    } finally {
      setBusyId("");
    }
  };

  // E7 — bulk selection helpers
  const toggleSelected = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const selectAllVisible = () => {
    setSelected(new Set(posts.map((p) => p.id)));
  };
  const clearSelection = () => setSelected(new Set());

  const runBulk = async (action) => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    const label = {
      approve: "approve & schedule",
      reject: "reject",
      needs_edits: "mark Needs Edits",
      publish_now: "publish NOW",
      unpublish: "unpublish",
      delete: "permanently delete",
    }[action] || action;
    if (!window.confirm(`${label} ${ids.length} post${ids.length === 1 ? "" : "s"}?`)) return;
    setBulkBusy(true);
    try {
      const res = await adminBulkAction(ids, action);
      toast.success(`${res.matched ?? ids.length} post${ids.length === 1 ? "" : "s"} updated.`);
      clearSelection();
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk action failed.");
    } finally {
      setBulkBusy(false);
    }
  };

  // E8 — preview link
  const copyPreviewLink = async (postId) => {
    setBusyId(postId);
    try {
      const res = await adminCreatePreviewToken(postId);
      const url = `${window.location.origin}${res.preview_path}`;
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Preview link copied to clipboard.");
      } catch {
        // Clipboard blocked — show the URL so the admin can copy manually.
        window.prompt("Copy this preview link:", url);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create preview link.");
    } finally {
      setBusyId("");
    }
  };

  const pendingCount = counts["Pending Review"] || 0;

  return (
    <main data-testid="admin-blog-page" className="bg-white min-h-screen">
      <SEO title="Blog Console" path="/admin/blog" noIndex />
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
          <div>
            <span className="ash-eyebrow">Admin · Blog Console</span>
            <h1 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight text-[#080808]">
              SEO Blog Content Agent
            </h1>
            <p className="mt-3 text-[#3C4048] max-w-2xl">
              Review, edit and approve the quarterly content batch. Approved
              posts schedule themselves for the next available Monday at 09:00
              UTC.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => load()}
              className="ash-btn-secondary"
              data-testid="admin-refresh-button"
            >
              <RefreshCw size={14} /> Refresh
            </button>
            <button
              type="button"
              onClick={onGenerate}
              disabled={generating}
              data-testid="admin-generate-button"
              className="ash-btn-primary disabled:opacity-60"
            >
              {generating ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <FileText size={14} />
              )}{" "}
              Generate Next Quarter
            </button>
            <a
              href="/admin/newsletters"
              data-testid="nav-to-newsletters"
              className="ash-link-underline inline-flex items-center gap-1.5"
            >
              <Mail size={14} /> Newsletters
            </a>
            <a
              href="/admin/comments"
              data-testid="nav-to-comments"
              className="ash-link-underline inline-flex items-center gap-1.5"
            >
              Comments
            </a>
            <a
              href="/admin/submissions"
              data-testid="nav-to-submissions"
              className="ash-link-underline inline-flex items-center gap-1.5"
            >
              Submissions
            </a>
            <a
              href="/admin/analytics"
              data-testid="nav-to-analytics"
              className="ash-link-underline inline-flex items-center gap-1.5"
            >
              Analytics
            </a>
            <a
              href="/admin/learning-hub"
              data-testid="nav-to-learning-hub"
              className="ash-link-underline inline-flex items-center gap-1.5"
            >
              Learning Hub
            </a>
            <a
              href="/admin/therabody"
              data-testid="nav-to-therabody"
              className="ash-link-underline inline-flex items-center gap-1.5"
            >
              Therabody
            </a>
            <a
              href="/admin/beta"
              data-testid="nav-to-beta"
              className="ash-link-underline inline-flex items-center gap-1.5"
            >
              Beta Preview
            </a>
            <a
              href="https://stats.uptimerobot.com/GivWo8aidQ"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-to-status"
              className="ash-link-underline inline-flex items-center gap-1.5"
            >
              System Status ↗
            </a>
            <a
              href="/admin/refresh"
              data-testid="nav-to-refresh"
              className="ash-link-underline inline-flex items-center gap-1.5"
            >
              <RotateCcw size={14} /> Refresh
            </a>
            <button
              type="button"
              onClick={onLogout}
              className="ash-link-underline"
              data-testid="admin-logout-button"
            >
              <LogOut size={14} /> Sign Out
            </button>
          </div>
        </div>

        {/* Banner */}
        {pendingCount > 0 && (
          <div
            data-testid="pending-banner"
            className="mb-8 flex items-center justify-between gap-6 border-l-4 border-[#5FA8B4] bg-[#F5F5F5] p-6"
          >
            <div>
              <div className="ash-eyebrow">Action Required</div>
              <p className="mt-2 text-[#080808] font-semibold text-lg">
                {pendingCount} blog{" "}
                {pendingCount === 1 ? "draft is" : "drafts are"} pending review.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setFilter("Pending Review")}
              className="ash-link-underline"
            >
              Review Now
            </button>
          </div>
        )}

        {/* Summary tiles */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-10">
            {STATUS_ORDER.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setFilter(filter === s ? "" : s)}
                data-testid={`filter-${s.toLowerCase().replace(/\s+/g, "-")}`}
                className={`text-left p-4 border transition-colors ${
                  filter === s
                    ? "border-[#3A7A8C] bg-[#F5F5F5]"
                    : "border-[#e5e7eb] hover:border-[#3A7A8C]"
                }`}
              >
                <div className="text-xs uppercase tracking-[0.18em] font-semibold text-[#6B7280]">
                  {s}
                </div>
                <div className="mt-1 text-2xl font-bold text-[#080808]">
                  {counts[s] || 0}
                </div>
              </button>
            ))}
            <button
              type="button"
              onClick={() => setFilter("")}
              className={`text-left p-4 border transition-colors ${
                !filter
                  ? "border-[#3A7A8C] bg-[#F5F5F5]"
                  : "border-[#e5e7eb] hover:border-[#3A7A8C]"
              }`}
            >
              <div className="text-xs uppercase tracking-[0.18em] font-semibold text-[#6B7280]">
                All
              </div>
              <div className="mt-1 text-2xl font-bold text-[#080808]">
                {summary?.totals?.all ?? 0}
              </div>
            </button>
          </div>
        )}

        {/* Posts table */}
        {loading ? (
          <p className="text-[#3C4048]">Loading…</p>
        ) : posts.length === 0 ? (
          <div className="border border-[#e5e7eb] p-12 text-[#3C4048]">
            No posts in this view. Click <strong>Generate Next Quarter</strong>{" "}
            to create 12 drafts.
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {/* E7 — Bulk action toolbar */}
            <div
              data-testid="bulk-toolbar"
              className={`flex flex-wrap items-center gap-3 border p-4 transition-colors ${
                selected.size > 0
                  ? "border-[#3A7A8C] bg-[#F0F7F9]"
                  : "border-[#e5e7eb] bg-white"
              }`}
            >
              <label className="inline-flex items-center gap-2 text-sm font-semibold text-[#080808] cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="bulk-select-all"
                  checked={selected.size > 0 && selected.size === posts.length}
                  ref={(el) => {
                    if (el) el.indeterminate = selected.size > 0 && selected.size < posts.length;
                  }}
                  onChange={(e) => (e.target.checked ? selectAllVisible() : clearSelection())}
                  className="w-4 h-4 accent-[#3A7A8C]"
                />
                {selected.size === 0
                  ? `Select all (${posts.length})`
                  : `${selected.size} of ${posts.length} selected`}
              </label>
              <div className="flex flex-wrap gap-2 items-center ml-auto">
                <button
                  type="button"
                  disabled={selected.size === 0 || bulkBusy}
                  onClick={() => runBulk("approve")}
                  data-testid="bulk-approve"
                  className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#5FA8B4] text-white font-bold uppercase tracking-[0.12em] text-xs hover:bg-[#3A7A8C] disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {bulkBusy ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
                  Approve + Schedule
                </button>
                <button
                  type="button"
                  disabled={selected.size === 0 || bulkBusy}
                  onClick={() => runBulk("publish_now")}
                  data-testid="bulk-publish-now"
                  className="inline-flex items-center gap-1.5 px-3 py-2 border border-[#3A7A8C] text-[#3A7A8C] font-bold uppercase tracking-[0.12em] text-xs hover:bg-[#F0F7F9] disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Publish Now
                </button>
                <button
                  type="button"
                  disabled={selected.size === 0 || bulkBusy}
                  onClick={() => runBulk("needs_edits")}
                  data-testid="bulk-needs-edits"
                  className="inline-flex items-center gap-1.5 px-3 py-2 border border-[#e5e7eb] text-[#080808] font-bold uppercase tracking-[0.12em] text-xs hover:border-[#3A7A8C] disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Edit3 size={12} /> Needs Edits
                </button>
                <button
                  type="button"
                  disabled={selected.size === 0 || bulkBusy}
                  onClick={() => runBulk("reject")}
                  data-testid="bulk-reject"
                  className="inline-flex items-center gap-1.5 px-3 py-2 border border-[#e5e7eb] text-[#991B1B] font-bold uppercase tracking-[0.12em] text-xs hover:border-[#991B1B] disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <XCircle size={12} /> Reject
                </button>
                <button
                  type="button"
                  disabled={selected.size === 0 || bulkBusy}
                  onClick={() => runBulk("delete")}
                  data-testid="bulk-delete"
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-[#991B1B] font-bold uppercase tracking-[0.12em] text-xs hover:underline disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Trash2 size={12} /> Delete
                </button>
                {selected.size > 0 && (
                  <button
                    type="button"
                    onClick={clearSelection}
                    className="ash-link-underline text-xs"
                    data-testid="bulk-clear"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>

            {posts.map((p) => {
              const isOpen = active === p.id;
              const isSelected = selected.has(p.id);
              return (
                <article
                  key={p.id}
                  data-testid={`post-row-${p.slug}`}
                  className={`border bg-white ${
                    isSelected ? "border-[#3A7A8C] shadow-[inset_2px_0_0_0_#3A7A8C]" : "border-[#e5e7eb]"
                  }`}
                >
                  <div className="p-6 flex flex-wrap items-center gap-4">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleSelected(p.id)}
                      data-testid={`bulk-check-${p.slug}`}
                      aria-label={`Select ${p.title}`}
                      className="w-4 h-4 accent-[#3A7A8C] mt-1 self-start"
                    />
                    <div className="flex-1 min-w-[260px]">
                      <div className="flex items-center gap-3 flex-wrap">
                        <StatusPill status={p.status} />
                        <span className="text-xs uppercase tracking-[0.18em] font-semibold text-[#3A7A8C]">
                          {p.category}
                        </span>
                        {p.scheduled_for && (
                          <span className="text-xs text-[#6B7280] flex items-center gap-1">
                            <CalendarClock size={12} />
                            {fmtDate(p.scheduled_for)}
                          </span>
                        )}
                        {p.published_at && (
                          <span className="text-xs text-[#166534] font-semibold">
                            Published {fmtDate(p.published_at)}
                          </span>
                        )}
                      </div>
                      <h3 className="mt-2 text-xl font-bold text-[#080808] leading-tight">
                        {p.title}
                      </h3>
                      {p.excerpt && (
                        <p className="mt-2 text-sm text-[#3C4048]">{p.excerpt}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {(p.status === "Pending Review" ||
                        p.status === "Needs Edits") && (
                        <button
                          type="button"
                          disabled={busyId === p.id}
                          onClick={() => doAction(p.id, "approve")}
                          data-testid={`approve-${p.slug}`}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-[#5FA8B4] text-white font-bold uppercase tracking-[0.12em] text-xs hover:bg-[#3A7A8C] disabled:opacity-60"
                        >
                          <CheckCircle2 size={14} /> Approve
                        </button>
                      )}
                      {(p.status === "Approved" || p.status === "Scheduled") && (
                        <button
                          type="button"
                          disabled={busyId === p.id}
                          onClick={() => {
                            if (
                              window.confirm(
                                `Publish "${p.title}" right now? This makes it public immediately.`,
                              )
                            ) {
                              doAction(p.id, "publish_now");
                            }
                          }}
                          data-testid={`publish-now-${p.slug}`}
                          title="Publish this post immediately (overrides the schedule)"
                          className="inline-flex items-center gap-2 px-4 py-2 bg-[#5FA8B4] text-white font-bold uppercase tracking-[0.12em] text-xs hover:bg-[#3A7A8C] disabled:opacity-60"
                        >
                          <Send size={14} /> Publish Now
                        </button>
                      )}
                      {p.status === "Published" && (
                        <button
                          type="button"
                          disabled={busyId === p.id}
                          onClick={() => {
                            if (
                              window.confirm(
                                `Unpublish "${p.title}"? It will revert to Approved status and be removed from the public site.`,
                              )
                            ) {
                              doAction(p.id, "unpublish");
                            }
                          }}
                          data-testid={`unpublish-${p.slug}`}
                          className="inline-flex items-center gap-2 px-3 py-2 border border-[#e5e7eb] text-[#080808] font-bold uppercase tracking-[0.12em] text-xs hover:border-[#3A7A8C] disabled:opacity-60"
                        >
                          <RotateCcw size={14} /> Unpublish
                        </button>
                      )}
                      {p.status !== "Rejected" && p.status !== "Published" && (
                        <button
                          type="button"
                          disabled={busyId === p.id}
                          onClick={() => doAction(p.id, "needs_edits")}
                          data-testid={`needs-edits-${p.slug}`}
                          className="inline-flex items-center gap-2 px-3 py-2 border border-[#e5e7eb] text-[#080808] font-bold uppercase tracking-[0.12em] text-xs hover:border-[#3A7A8C] disabled:opacity-60"
                        >
                          <Edit3 size={14} /> Needs Edits
                        </button>
                      )}
                      {p.status !== "Rejected" && (
                        <button
                          type="button"
                          disabled={busyId === p.id}
                          onClick={() => doAction(p.id, "reject")}
                          data-testid={`reject-${p.slug}`}
                          className="inline-flex items-center gap-2 px-3 py-2 border border-[#e5e7eb] text-[#991B1B] font-bold uppercase tracking-[0.12em] text-xs hover:border-[#991B1B] disabled:opacity-60"
                        >
                          <XCircle size={14} /> Reject
                        </button>
                      )}
                      {p.status === "Rejected" && (
                        <button
                          type="button"
                          disabled={busyId === p.id}
                          onClick={() => doAction(p.id, "needs_edits")}
                          className="inline-flex items-center gap-2 px-3 py-2 border border-[#e5e7eb] text-[#080808] font-bold uppercase tracking-[0.12em] text-xs"
                        >
                          <RotateCcw size={14} /> Reopen
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => copyPreviewLink(p.id)}
                        disabled={busyId === p.id}
                        data-testid={`preview-link-${p.slug}`}
                        title="Copy a shareable preview link for this post (works even when unpublished)"
                        className="inline-flex items-center gap-1.5 px-3 py-2 border border-[#e5e7eb] text-[#3A7A8C] font-bold uppercase tracking-[0.12em] text-xs hover:border-[#3A7A8C] disabled:opacity-60"
                      >
                        {busyId === p.id ? <Loader2 size={12} className="animate-spin" /> : <LinkIcon size={12} />}
                        Preview Link
                      </button>
                      <button
                        type="button"
                        onClick={() => (isOpen ? setActive(null) : beginEdit(p))}
                        data-testid={`expand-${p.slug}`}
                        className="ash-link-underline"
                      >
                        {isOpen ? "Close" : "View / Edit"}
                      </button>
                    </div>
                  </div>

                  {isOpen && (
                    <div className="border-t border-[#e5e7eb] p-6 bg-[#FAFAFA]">
                      <div className="grid lg:grid-cols-2 gap-8">
                        <div className="flex flex-col gap-4">
                          <div>
                            <label className="ash-label" htmlFor={`t-${p.id}`}>
                              Title
                            </label>
                            <input
                              id={`t-${p.id}`}
                              className="ash-input"
                              value={editForm.title || ""}
                              onChange={(e) =>
                                setEditForm({
                                  ...editForm,
                                  title: e.target.value,
                                })
                              }
                            />
                          </div>
                          <div>
                            <label className="ash-label">SEO Title</label>
                            <input
                              className="ash-input"
                              value={editForm.seo_title || ""}
                              onChange={(e) =>
                                setEditForm({
                                  ...editForm,
                                  seo_title: e.target.value,
                                })
                              }
                            />
                          </div>
                          <div>
                            <label className="ash-label">Meta Description</label>
                            <textarea
                              className="ash-input ash-textarea !min-h-[90px]"
                              value={editForm.meta_description || ""}
                              onChange={(e) =>
                                setEditForm({
                                  ...editForm,
                                  meta_description: e.target.value,
                                })
                              }
                            />
                          </div>
                          <div>
                            <label className="ash-label">URL Slug</label>
                            <input
                              className="ash-input"
                              value={editForm.slug || ""}
                              onChange={(e) =>
                                setEditForm({
                                  ...editForm,
                                  slug: e.target.value,
                                })
                              }
                            />
                          </div>
                          <div>
                            <label className="ash-label">Excerpt</label>
                            <textarea
                              className="ash-input ash-textarea !min-h-[80px]"
                              value={editForm.excerpt || ""}
                              onChange={(e) =>
                                setEditForm({
                                  ...editForm,
                                  excerpt: e.target.value,
                                })
                              }
                            />
                          </div>
                          <div>
                            <label className="ash-label">
                              Featured Image URL
                            </label>
                            <input
                              className="ash-input"
                              value={editForm.featured_image_url || ""}
                              onChange={(e) =>
                                setEditForm({
                                  ...editForm,
                                  featured_image_url: e.target.value,
                                })
                              }
                            />
                          </div>
                          <div>
                            <label className="ash-label">
                              Admin Notes (internal)
                            </label>
                            <textarea
                              className="ash-input ash-textarea !min-h-[80px]"
                              value={editForm.admin_notes || ""}
                              onChange={(e) =>
                                setEditForm({
                                  ...editForm,
                                  admin_notes: e.target.value,
                                })
                              }
                            />
                          </div>
                        </div>

                        <div>
                          <label className="ash-label">Content (Markdown)</label>
                          <textarea
                            className="w-full bg-white border border-[#e5e7eb] p-4 font-mono text-xs leading-relaxed text-[#080808] focus:border-[#3A7A8C] outline-none"
                            style={{ minHeight: 360 }}
                            value={editForm.content_markdown || ""}
                            onChange={(e) =>
                              setEditForm({
                                ...editForm,
                                content_markdown: e.target.value,
                              })
                            }
                            data-testid={`content-editor-${p.slug}`}
                          />
                          <div className="mt-4 p-4 border border-[#e5e7eb] bg-white max-h-72 overflow-auto">
                            <div className="ash-eyebrow mb-2">Preview</div>
                            <div className="prose-ashlan-sm">
                              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                {editForm.content_markdown || ""}
                              </ReactMarkdown>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="mt-6 flex flex-wrap items-center gap-3">
                        <button
                          type="button"
                          disabled={busyId === p.id}
                          onClick={() => saveEdit(p.id)}
                          data-testid={`save-${p.slug}`}
                          className="ash-btn-primary disabled:opacity-60"
                        >
                          Save Changes
                        </button>
                        <button
                          type="button"
                          onClick={() => setActive(null)}
                          className="ash-link-underline"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        )}
      </div>
    </main>
  );
}
